import classNames from "classnames"
import Image from "next/image"
import React from "react"

import { Paragraph1, Title2 } from "@bounce/design-system"
import { useBounceGlobalValues, useTranslation } from "@bounce/i18n"
import { App } from "@bounce/util"
import { Button } from "@bounce/web-components"

import { useUrl } from "../../../../libs/router"
import { useAppStoreUrl } from "@/hooks/useAppStoreUrl"
import { useSectionViewed } from "@/hooks/useSectionViewed"
import { useAppURL } from "@/utils/appURL"

import AppDownloadIllo from "./assets/app-download-illo.webp"
import AppStoreBadge from "./assets/app-store-badge.svg"
import PlayStoreBadge from "./assets/play-store-badge.svg"

type AppBannerTertiaryProps = {
  className?: string
  subtitle?: string
  title?: string
}

export const AppBannerTertiary = ({ className, subtitle, title }: AppBannerTertiaryProps) => {
  const { t } = useTranslation()
  const { numCities } = useBounceGlobalValues()
  const { sectionRef } = useSectionViewed({
    name: "AppBannerLuggageStorageTertiary",
  })
  const buildAppUrl = useAppURL()
  const { getUrl } = useUrl()
  const { href, onClick } = useAppStoreUrl({
    app: App.Customer,
    path: buildAppUrl(getUrl({ path: "/s" }), { coupon: "GET5WITHBOUNCE" }),
    eventProps: {
      location: "AppBannerLuggageStorageTertiary",
    },
  })

  return (
    <section ref={sectionRef} className={classNames("w-full bg-cranberry-700 @container", className)}>
      <div className="grid @md:grid-cols-[calc((100%-1280px)/2)_auto_1fr]">
        <div className="max-w-screen-xl px-6 pt-10 @md:col-start-2 @md:col-end-3 @md:w-full @md:px-8 @md:py-20">
          <div className="max-w-[440px]">
            <Title2 className="text-white">
              {title ?? t("cmp.appBannerTertiary.title", "Drop off your bags faster with the app")}
            </Title2>
            <Paragraph1 className="mt-3 text-white">
              {subtitle ??
                t(
                  "cmp.appBannerTertiary.subtitle",
                  "Get the app and find storage spots in over {NUM_CITIES} cities around the world.",
                  {
                    NUM_CITIES: numCities,
                  },
                )}
            </Paragraph1>
            <Button
              href={href}
              onClick={onClick}
              className="mt-9 w-full bg-zest-700 text-primary-700 hover:bg-zest-700 @md:w-fit">
              {t("cmp.appBannerTertiary.cta", "Get the app")}
            </Button>
            <div className="mt-6 flex items-center justify-center gap-7 @md:justify-start">
              <AppStoreBadge className="w-24" onClick={onClick} />
              <PlayStoreBadge className="w-24" onClick={onClick} />
            </div>
          </div>
        </div>
        <div className="@md:col-start-3 @md:col-end-4 @md:flex @md:justify-end">
          <Image
            alt={t(
              "cmp.appBannerTertiary.imageAlt",
              "A young man with grey hair wearing a shirt and a bag on his shoulder is using the Bounce app on his mobile phone.",
            )}
            width={640}
            height={640}
            src={AppDownloadIllo}
            className="min-w-[170px] self-end"
          />
        </div>
      </div>
    </section>
  )
}
